import { useState } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Trash2 } from 'lucide-react';

import { Button, Card, Textarea, cn } from '@llura/ui';
import type { MoodKey } from '@llura/constants';

import { useStrings } from '../../hooks/useStrings';
import { useHaptic } from '../../hooks/useHaptic';
import { useJournalStore, type JournalEntry } from '../../stores/journal.store';

const MOOD_ORDER: readonly MoodKey[] = ['aroused', 'challenged', 'relaxed', 'connected', 'empty'];

/**
 * Private journal — write a reflection right after the afterglow, and
 * browse what you've captured before. Phase 1 stores entries plain-text in
 * localStorage; encryption + server-backed sync are explicit phase-2 work.
 */
export function JournalPage() {
  const navigate = useNavigate();
  const { strings } = useStrings();
  const haptic = useHaptic();
  const entries = useJournalStore((s) => s.entries);
  const addEntry = useJournalStore((s) => s.addEntry);
  const deleteEntry = useJournalStore((s) => s.deleteEntry);

  const [body, setBody] = useState('');
  const [mood, setMood] = useState<MoodKey | null>(null);

  const canSave = body.trim().length > 0;

  const save = () => {
    if (!canSave) return;
    haptic.success();
    addEntry({ body, mood });
    setBody('');
    setMood(null);
  };

  const remove = (id: string) => {
    if (!confirm(strings.journal.deleteConfirm)) return;
    haptic.tap();
    deleteEntry(id);
  };

  return (
    <div className="mx-auto w-full max-w-md space-y-6 px-4 py-5">
      <header className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/profile')}
          className="rounded-full"
          aria-label={strings.journal.backAria}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
            {strings.journal.eyebrow}
          </p>
          <h1 className="font-serif text-2xl leading-tight">{strings.journal.title}</h1>
        </div>
      </header>

      <Card className="space-y-4 border-border bg-card p-4">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={strings.journal.placeholder}
          rows={4}
          className="resize-none bg-background"
        />

        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {strings.journal.moodPrompt}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {MOOD_ORDER.map((key) => {
              const active = mood === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMood(active ? null : key)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs transition-all',
                    active
                      ? 'border-primary bg-primary/15 text-foreground'
                      : 'border-border bg-transparent text-muted-foreground hover:border-primary/40',
                  )}
                >
                  {strings.journal.moods[key]}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          onClick={save}
          disabled={!canSave}
          className="h-11 w-full rounded-full bg-primary text-primary-foreground glow-primary disabled:opacity-40"
        >
          {strings.journal.saveCta}
        </Button>
      </Card>

      <section className="space-y-3">
        <h2 className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          {strings.journal.listTitle}
        </h2>
        {entries.length === 0 ? (
          <p className="px-1 text-sm text-muted-foreground">{strings.journal.listEmpty}</p>
        ) : (
          <ul className="space-y-2">
            <AnimatePresence initial={false}>
              {entries.map((entry) => (
                <EntryItem
                  key={entry.id}
                  entry={entry}
                  onDelete={() => remove(entry.id)}
                />
              ))}
            </AnimatePresence>
          </ul>
        )}
      </section>
    </div>
  );
}

function EntryItem({ entry, onDelete }: { entry: JournalEntry; onDelete: () => void }) {
  const { strings } = useStrings();
  const moodLabel = entry.mood ? strings.journal.moods[entry.mood] : null;
  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <Card className="space-y-2 border-border bg-card p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            {formatDate(entry.at)}
            {moodLabel && <span className="ml-2 normal-case tracking-normal">· {moodLabel}</span>}
          </div>
          <button
            type="button"
            onClick={onDelete}
            aria-label={strings.journal.deleteAria}
            className="text-muted-foreground/60 transition-colors hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="whitespace-pre-wrap break-words text-sm text-foreground/90">{entry.body}</p>
      </Card>
    </motion.li>
  );
}

function formatDate(at: number): string {
  const d = new Date(at);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
